import React, { useRef, useEffect, useCallback } from "react";
import { hsvToHex } from "./colorUtils";

/**
 * Saturation/Brightness gradient square.
 * X axis = saturation (0..1), Y axis = brightness (1..0, top to bottom).
 *
 * @param {object}   props
 * @param {number}   props.hue        - current hue [0, 360)
 * @param {number}   props.saturation - [0, 1]
 * @param {number}   props.brightness - [0, 1]
 * @param {Function} props.onChange   - (s: number, v: number) => void
 */
const ColorPickerCanvas = ({ hue, saturation, brightness, onChange }) => {
  const canvasRef = useRef(null);
  const isDragging = useRef(false);

  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;

    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const { width, height } = canvas;

    // White → hue gradient (left to right)
    const hueColor = hsvToHex(hue, 1, 1);
    const gradientH = ctx.createLinearGradient(0, 0, width, 0);

    gradientH.addColorStop(0, "#ffffff");
    gradientH.addColorStop(1, hueColor);
    ctx.fillStyle = gradientH;
    ctx.fillRect(0, 0, width, height);

    // Transparent → black gradient (top to bottom)
    const gradientV = ctx.createLinearGradient(0, 0, 0, height);

    gradientV.addColorStop(0, "rgba(0,0,0,0)");
    gradientV.addColorStop(1, "rgba(0,0,0,1)");
    ctx.fillStyle = gradientV;
    ctx.fillRect(0, 0, width, height);
  }, [hue]);

  useEffect(() => {
    drawCanvas();
  }, [drawCanvas]);

  const applyPointer = useCallback(
    (clientX, clientY) => {
      const canvas = canvasRef.current;

      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      const s = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
      const v = Math.min(1, Math.max(0, 1 - (clientY - rect.top) / rect.height));

      onChange(s, v);
    },
    [onChange],
  );

  // Pointer events with capture: a finger drags here too, also past the edges
  const handlePointerDown = (e) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    isDragging.current = true;
    applyPointer(e.clientX, e.clientY);
  };

  const handlePointerMove = (e) => {
    if (isDragging.current) applyPointer(e.clientX, e.clientY);
  };

  const handlePointerEnd = () => {
    isDragging.current = false;
  };

  const cursorX = `${saturation * 100}%`;
  const cursorY = `${(1 - brightness) * 100}%`;

  return (
    <div className="color-picker-canvas">
      <canvas
        ref={canvasRef}
        className="color-picker-canvas__canvas"
        width={348}
        height={120}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerEnd}
        onPointerCancel={handlePointerEnd}
      />
      <div
        className="color-picker-canvas__cursor"
        style={{ left: cursorX, top: cursorY }}
        aria-hidden="true"
      />
    </div>
  );
};

export default ColorPickerCanvas;
